import {
	IconCheck,
	IconClockOff,
	IconLock,
	IconMinus,
	IconShieldLock,
	IconUserOff,
	IconX,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

const faqs = [
	{
		question: "Is Mailinator free?",
		answer:
			"Mailinator's public inboxes at @mailinator.com are free, but severely limited. Private inboxes, API access, webhooks, and custom domains require a paid plan starting at $99/month. The free tier also has a 200 email-read-per-day cap and no account isolation — anyone who knows the address can read the inbox.",
	},
	{
		question: "Why is Mailinator blocked by so many websites?",
		answer:
			"Mailinator domains are on most disposable-email blocklists maintained by fraud prevention providers. Amazon Seller Central, Stripe, many SaaS products, and financial services reject @mailinator.com addresses at sign-up. This happens because the domains are publicly known as test/throwaway inboxes. Services on less widely-blocked domains — like ineedatest.email — get through more often.",
	},
	{
		question: "Can I use ineedatest.email instead of Mailinator for testing?",
		answer:
			"If your testing workflow is manual — open a browser, spin up an address, receive an OTP or verification email, move on — then yes, ineedatest.email works well. It has no API or webhooks, so it is not a drop-in replacement for automated CI/CD test suites that use Mailinator's programmatic inbox access. For automated testing, purpose-built tools like Mailosaur or Mailtrap are better fits.",
	},
	{
		question: "Are ineedatest.email inboxes private?",
		answer:
			"Yes. Unlike Mailinator's free public inboxes — where anyone who guesses or knows the address can read the mail — ineedatest.email inboxes are accessed only via the direct inbox URL. There is no public directory and no search. If someone does not have your inbox URL, they cannot find or read your messages.",
	},
	{
		question: "Does ineedatest.email expire my inbox?",
		answer:
			"Yes, and that is the point. You set the expiry when you create the address. When the timer reaches zero, the inbox is gone — not hidden behind a paywall or sitting in an account you forgot about. Honest throwaway behavior, not marketing fine print.",
	},
];

const comparisonRows: {
	feature: string;
	ours: { state: "yes" | "no" | "partial"; note?: string };
	theirs: { state: "yes" | "no" | "partial"; note?: string };
}[] = [
	{
		feature: "Completely free",
		ours: { state: "yes", note: "No paid tiers, no caps" },
		theirs: {
			state: "partial",
			note: "Free tier is public-only, 200 reads/day",
		},
	},
	{
		feature: "Account required",
		ours: { state: "no", note: "Inbox URL is the credential" },
		theirs: { state: "partial", note: "No for public, yes for private or API" },
	},
	{
		feature: "Private inboxes",
		ours: { state: "yes", note: "No public directory" },
		theirs: { state: "partial", note: "Paid plans only ($99+/mo)" },
	},
	{
		feature: "User-controlled expiry",
		ours: { state: "yes", note: "Set your own timer" },
		theirs: {
			state: "partial",
			note: "Auto-purge ~1 hr free; manual delete paid",
		},
	},
	{
		feature: "API access",
		ours: { state: "no" },
		theirs: { state: "partial", note: "Paid plans only" },
	},
	{
		feature: "Webhooks",
		ours: { state: "no" },
		theirs: { state: "partial", note: "Paid plans only" },
	},
	{
		feature: "Outbound sending",
		ours: { state: "no", note: "Inbound-only by design" },
		theirs: { state: "no" },
	},
	{
		feature: "Multiple domains",
		ours: { state: "yes", note: "Several receiving domains" },
		theirs: { state: "partial", note: "Custom domains are paid" },
	},
	{
		feature: "Blocked by major services",
		ours: { state: "partial", note: "Less common than Mailinator" },
		theirs: { state: "yes", note: "Blocked by Amazon, Stripe, many SaaS" },
	},
	{
		feature: "Attachment storage",
		ours: { state: "no", note: "Text and HTML only" },
		theirs: { state: "partial", note: "Paid plans" },
	},
	{
		feature: "Team accounts",
		ours: { state: "no" },
		theirs: { state: "partial", note: "Paid plans" },
	},
	{
		feature: "SMS testing",
		ours: { state: "no" },
		theirs: { state: "partial", note: "Paid plans" },
	},
];

const switchReasons = [
	{
		icon: IconLock,
		title: "Private inboxes without a credit card",
		body: "Mailinator's free public inboxes are readable by anyone — not just you. Anyone who can guess your test address can monitor it. ineedatest.email inboxes are private: the URL is the credential. Nobody else can search for or browse to your inbox. And it costs nothing.",
	},
	{
		icon: IconUserOff,
		title: "No account, no onboarding, no friction",
		body: "Mailinator requires account creation for anything beyond basic public inbox access. ineedatest.email has no accounts. Spin up an address, paste it, get the email, move on. Zero sign-up steps. Useful for the moment you need a real inbox but do not want another tab open to manage it.",
	},
	{
		icon: IconShieldLock,
		title: "Domains that actually get through",
		body: "Mailinator domains appear on virtually every disposable-email blocklist. Amazon, Stripe, many SaaS products, and financial services reject @mailinator.com at the sign-up form. ineedatest.email offers multiple receiving domains, and less widely-known domains are blocked less frequently — they work for more of the trials and sign-ups you actually care about.",
	},
	{
		icon: IconClockOff,
		title: "Honest expiry with no surprises",
		body: "ineedatest.email is built around explicit expiry: you set the timer, the inbox disappears when it runs out. No account accumulation, no forgotten test addresses lingering in a dashboard. Mailinator's free tier auto-purges after roughly an hour with no user control. Paid tiers give more control but require a subscription for what is essentially a throwaway inbox.",
	},
];

const honestyRows = [
	{
		feature: "API access and CI/CD automation",
		body: "Mailinator was built for automated email testing. Its REST API, webhook routing rules, and integrations with Playwright, Selenium, and Cypress are purpose-built for engineering teams running email assertions in CI pipelines. ineedatest.email has no API and no webhooks — it is a browser tool, not a test infrastructure tool.",
	},
	{
		feature: "Team features and shared inboxes",
		body: "Mailinator's paid plans offer team accounts, access control, and shared private domains — useful for QA teams who need consistent, shared test infrastructure. ineedatest.email has no team features whatsoever. It is a single-user, stateless tool.",
	},
	{
		feature: "Persistent inbox history",
		body: "ineedatest.email inboxes expire by design. If you need to reference test emails days or weeks later — audit trails, regression debugging, compliance testing — Mailinator's paid plan retains messages. Our inbox is gone when the timer expires; intentionally so, but that is a real limitation.",
	},
];

function StateCell({
	state,
	note,
}: {
	state: "yes" | "no" | "partial";
	note?: string;
}) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="flex items-center gap-1.5">
				{state === "yes" && (
					<IconCheck className="size-4 shrink-0 text-green-600 dark:text-green-500" />
				)}
				{state === "no" && (
					<IconX className="size-4 shrink-0 text-destructive" />
				)}
				{state === "partial" && (
					<IconMinus className="size-4 shrink-0 text-amber-500" />
				)}
				<span className="text-xs font-medium text-foreground capitalize">
					{state === "partial" ? "limited" : state}
				</span>
			</span>
			{note && (
				<span className="text-xs text-muted-foreground leading-snug">
					{note}
				</span>
			)}
		</div>
	);
}

export const Route = createFileRoute("/_marketing/alternatives/mailinator")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Mailinator alternative — ineedatest.email",
			description:
				"Free private disposable inboxes with no account, no API required. See how ineedatest.email compares to Mailinator for OTPs, trials, and sign-ups.",
			keywords:
				"mailinator alternative, mailinator alternatives, alternative to mailinator, disposable email, temporary email, private inbox",
			type: "FAQPage",
			faqItems: faqs,
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			{/* Hero */}
			<section className="pb-12 pt-20 sm:pb-16 sm:pt-28">
				<MarketingContainer>
					<nav className="mb-6 text-xs text-muted-foreground">
						<a href="/" className="hover:text-foreground">
							ineedatest.email
						</a>
						<span className="mx-2">/</span>
						<span>Mailinator alternative</span>
					</nav>
					<div className={cn("max-w-3xl", MARKETING_SECTION_ANIM_BASE)}>
						<p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
							Mailinator alternative
						</p>
						<h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
							Looking for a Mailinator alternative?
						</h1>
						<p className="text-lg text-pretty text-muted-foreground sm:text-xl">
							Mailinator's free tier puts your test emails in a public inbox
							anyone can read — and private access starts at $99/month.
							ineedatest.email gives you private, expiring disposable inboxes
							for free, with no account required.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* Comparison table */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Comparison"
						heading="Best Mailinator alternatives in 2026"
						description="A feature-by-feature look at how ineedatest.email stacks up against Mailinator for the most common disposable-email jobs: receiving OTPs, testing sign-up flows, and staying out of real inboxes."
						className="mb-10 sm:mb-12"
					/>
					<div className="overflow-x-auto rounded-xl border border-border/60">
						<table className="w-full min-w-[560px] text-sm">
							<thead>
								<tr className="border-b border-border/60 bg-muted/30">
									<th className="py-3 pl-5 pr-4 text-left font-semibold text-foreground">
										Feature
									</th>
									<th className="px-4 py-3 text-left font-semibold text-primary">
										ineedatest.email
									</th>
									<th className="px-4 py-3 text-left font-semibold text-foreground">
										Mailinator
									</th>
								</tr>
							</thead>
							<tbody>
								{comparisonRows.map((row, i) => (
									<tr
										key={row.feature}
										className={cn(
											"border-b border-border/40 last:border-0",
											i % 2 === 0 ? "bg-transparent" : "bg-muted/10",
										)}
									>
										<td className="py-3 pl-5 pr-4 font-medium text-foreground">
											{row.feature}
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.ours} />
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.theirs} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<p className="mt-3 text-xs text-muted-foreground">
						Mailinator pricing sourced from their public site. Accurate as of
						May 2026.
					</p>
				</MarketingContainer>
			</section>

			{/* Why switch */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Why switch"
						heading="When ineedatest.email fits better than Mailinator"
						description="Mailinator was designed for QA teams with budgets and CI pipelines. For everyone else — quick trials, one-time OTPs, sign-up forms you will regret — here is why the simpler tool wins."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
						{switchReasons.map((reason, index) => {
							const Icon = reason.icon;
							return (
								<div
									key={reason.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.1}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-2">
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{reason.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{reason.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* Honesty section */}
			<section className="border-t border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Honest take"
						heading="Where Mailinator still wins"
						description="This is a side project, not a Mailinator killer. Three real situations where Mailinator is the better choice."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
						{honestyRows.map((row) => (
							<div
								key={row.feature}
								className="rounded-xl border border-border/60 bg-card/30 p-6 ring-1 ring-border/30"
							>
								<h3 className="mb-2 text-sm font-semibold text-foreground">
									{row.feature}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground">
									{row.body}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* FAQ */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="FAQ"
						heading="Questions about switching from Mailinator"
						className="mb-10 sm:mb-12"
					/>
					<div className="divide-y divide-border/50">
						{faqs.map((faq) => (
							<div key={faq.question} className="py-6 first:pt-0 last:pb-0">
								<h3 className="mb-2 text-base font-semibold text-foreground">
									{faq.question}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* CTA + sibling links */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-base font-semibold text-foreground">
								Try ineedatest.email — no account, no setup
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								Open the homepage, pick a domain, set a timer, copy the address.
							</p>
						</div>
						<a
							href="/"
							className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
						>
							Get a disposable inbox
						</a>
					</div>

					<div className="mt-12 border-t border-border/50 pt-8">
						<p className="mb-3 text-sm text-muted-foreground">
							See what ineedatest.email can do:{" "}
							<a
								href="/features"
								className="text-primary underline-offset-4 hover:underline"
							>
								product features
							</a>
						</p>
						<p className="mb-4 text-sm font-medium text-muted-foreground">
							Compare other disposable email services
						</p>
						<div className="flex flex-wrap gap-3">
							<a
								href="/alternatives/guerrilla-mail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Guerrilla Mail alternatives
							</a>
							<a
								href="/alternatives/temp-mail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Temp Mail alternatives
							</a>
							<a
								href="/alternatives/10minutemail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								10MinuteMail alternatives
							</a>
						</div>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
